import React from 'react';

import {Button} from '@deviceinsight/ng-ui-basic-components';
import {GridColorPicker} from '@deviceinsight/ng-ui-components';

import {Trans, StyleApi, WidgetProps} from '@deviceinsight/ng-ui-scale-lib/api';
import SelectAssetButton from '../../common/components/SelectAssetButton';
import SelectKpiButton from '../../common/components/SelectKpiButton';
import EnumDropdown from '../../common/components/EnumDropdown';
import {KpiQuery} from '../../common/kpiUtils';
import {KpiSingleValueWidgetConfig} from '../types';
import useAssetsByUrns from '../../common/hooks/useAssetsByUrns';
import TimeConfig from '../../common/components/TimeConfig';
import DecimalPlacesConfigRow from '../../../DecimalPlacesConfigRow';

type Props = {
	context: WidgetProps['context'];
	config: KpiSingleValueWidgetConfig;
	onChangeConfig: (arg0: KpiSingleValueWidgetConfig) => void;
	setSelectingAsset: () => void;
	setSelectingKpi: (assetUrn: string) => void;
};

const KpiSingleValueWidgetConfigContent = ({
	context,
	config,
	config: {settings},
	onChangeConfig,
	setSelectingAsset,
	setSelectingKpi
}: Props) => {
	const isAssetGroupContext = context.type === 'assetGroup';
	const kpiButtonDisabled = isAssetGroupContext && !settings.assetUrn;
	const assets = useAssetsByUrns(settings.assetUrn ? [settings.assetUrn] : []);
	return (
		<div className="di form kpi-single-value-widget-config">
			<div className="fields">
				<div className="field">
					<div className="standard wide field">
						<label>
							<Trans i18nKey="dashboard.widgets.config.boxHeadline">Headline</Trans>
						</label>
						<input
							data-testid="title-input"
							type="text"
							value={config.title}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
								onChangeConfig({...config, title: e.target.value})
							}
						/>
					</div>
				</div>
				<div className="field">
					<div className="standard wide field">
						<label>
							<Trans i18nKey="dashboard.widgets.config.subtitle">Subtitle</Trans>
						</label>
						<input
							data-testid="subtitle-input"
							type="text"
							value={config.settings.subtitle}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
								onChangeConfig({...config, settings: {...config.settings, subtitle: e.target.value}})
							}
						/>
					</div>
				</div>
			</div>
			<div className="fields">
				{isAssetGroupContext && (
					<div className="field">
						<label>
							<Trans i18nKey="dashboard.widgets.kpisinglevalue.config.asset">Asset</Trans>
						</label>
						<SelectAssetButton
							assetName={assets[0]?.name ?? ''}
							selectAsset={setSelectingAsset}
							data-testid="select-asset-button"
						/>
					</div>
				)}
				<div className="field">
					<label>
						<Trans i18nKey="dashboard.widgets.kpisinglevalue.config.kpiname">Kpi Name</Trans>
					</label>
					<SelectKpiButton
						data-testid="select-kpi-button"
						kpiName={settings.kpiName}
						selectKpi={() => {
							const urn = context.type === 'asset' ? context.asset.urn : settings.assetUrn;
							if (urn) setSelectingKpi(urn);
						}}
						disabled={kpiButtonDisabled}
					/>
				</div>
				<div className="field">
					<label>
						<Trans i18nKey="dashboard.widgets.kpisinglevalue.config.color">Color</Trans>
					</label>
					<GridColorPicker
						inline={false}
						colors={StyleApi.getColorPalette()}
						value={settings.color || ''}
						onPick={(color: string) => onChangeConfig({...config, settings: {...config.settings, color}})}
					/>
				</div>
				{settings.color != null && (
					<div
						className="field"
						style={{
							paddingTop: 20
						}}>
						<Button
							onClick={() =>
								onChangeConfig({...config, settings: {...config.settings, color: undefined}})
							}>
							<Trans i18nKey="dashboard.widgets.kpisinglevalue.config.clearColor">Clear</Trans>
						</Button>
					</div>
				)}
			</div>
			<div className="fields">
				<div className="field four wide">
					<label>
						<Trans i18nKey="dashboard.widgets.kpisinglevalue.config.unite">Unit</Trans>
					</label>
					<input
						data-testid="unit-input"
						type="text"
						value={settings.unit || ''}
						onChange={({target: {value}}: React.ChangeEvent<HTMLInputElement>) =>
							onChangeConfig({...config, settings: {...config.settings, unit: value}})
						}
					/>
				</div>
			</div>
			<DecimalPlacesConfigRow
				fieldClassName="four wide"
				digits={settings.maximumFractionDigits ?? 0}
				onChange={(maximumFractionDigits) => {
					onChangeConfig({
						...config,
						settings: {
							...config.settings,
							maximumFractionDigits
						}
					});
				}}
			/>
			<div className="fields">
				<div className="field four wide">
					<label>
						<Trans i18nKey="dashboard.widgets.kpisinglevalue.config.aggregationtype">
							Aggregation type
						</Trans>
					</label>
					<EnumDropdown
						data-testid="aggregation-type-select"
						toFilter={['ALL']}
						options={KpiQuery}
						value={settings.aggregationType}
						onChange={(aggregationType) =>
							onChangeConfig({...config, settings: {...config.settings, aggregationType}})
						}
					/>
				</div>
			</div>
			<TimeConfig
				periodTimeframe={settings.periodTimeframe}
				periodType={settings.periodType}
				periodCount={settings.periodCount}
				customPeriodFrom={settings.customPeriodFrom}
				customPeriodTo={settings.customPeriodTo}
				workingShiftEnabled={settings.workingShiftEnabled}
				workingShiftTimeFrom={settings.workingShiftTimeFrom}
				workingShiftTimeTo={settings.workingShiftTimeTo}
				workingShiftDays={settings.workingShiftDays}
				onPeriodTimeframePeriodTypeChange={(periodTimeframe, periodType) =>
					onChangeConfig({...config, settings: {...config.settings, periodTimeframe, periodType}})
				}
				onPeriodCountChange={(periodCount) =>
					onChangeConfig({...config, settings: {...config.settings, periodCount}})
				}
				onCustomPeriodChange={(customPeriodFrom, customPeriodTo) =>
					onChangeConfig({...config, settings: {...config.settings, customPeriodFrom, customPeriodTo}})
				}
				onWorkingShiftEnabledChange={(workingShiftEnabled) =>
					onChangeConfig({...config, settings: {...config.settings, workingShiftEnabled}})
				}
				onWorkingShiftTimeChange={(workingShiftTimeFrom, workingShiftTimeTo) =>
					onChangeConfig({
						...config,
						settings: {...config.settings, workingShiftTimeFrom, workingShiftTimeTo}
					})
				}
				onWorkingShiftDaysChange={(workingShiftDays) =>
					onChangeConfig({...config, settings: {...config.settings, workingShiftDays}})
				}
			/>
		</div>
	);
};

export default KpiSingleValueWidgetConfigContent;
